/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `totals` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class TotalsMesg : Mesg {
    public constructor() : super("Totals", Profile.MesgNum.TOTALS)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timer_time, in s. Excludes pauses */
    public var timerTime: UInt?
        get() = getFieldValue(TIMER_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TIMER_TIME_FIELD_NUM, value)

    /** distance, in m. */
    public var distance: UInt?
        get() = getFieldValue(DISTANCE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DISTANCE_FIELD_NUM, value)

    /** calories, in kcal. */
    public var calories: UInt?
        get() = getFieldValue(CALORIES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CALORIES_FIELD_NUM, value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** elapsed_time, in s. Includes pauses */
    public var elapsedTime: UInt?
        get() = getFieldValue(ELAPSED_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(ELAPSED_TIME_FIELD_NUM, value)

    /** sessions. */
    public var sessions: UShort?
        get() = getFieldValue(SESSIONS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SESSIONS_FIELD_NUM, value)

    /** active_time, in s. */
    public var activeTime: UInt?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** sport_index. */
    public var sportIndex: UByte?
        get() = getFieldValue(SPORT_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SPORT_INDEX_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMER_TIME_FIELD_NUM: UByte = 0u
        public const val DISTANCE_FIELD_NUM: UByte = 1u
        public const val CALORIES_FIELD_NUM: UByte = 2u
        public const val SPORT_FIELD_NUM: UByte = 3u
        public const val ELAPSED_TIME_FIELD_NUM: UByte = 4u
        public const val SESSIONS_FIELD_NUM: UByte = 5u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 6u
        public const val SPORT_INDEX_FIELD_NUM: UByte = 9u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimerTime", 0u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[1] = Field("Distance", 1u, BaseType.UINT32, 1.0, 0.0, "m", false)
            fields[2] = Field("Calories", 2u, BaseType.UINT32, 1.0, 0.0, "kcal", false)
            fields[3] = Field("Sport", 3u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[4] = Field("ElapsedTime", 4u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[5] = Field("Sessions", 5u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[6] = Field("ActiveTime", 6u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[9] = Field("SportIndex", 9u, BaseType.UINT8, 1.0, 0.0, "", false)
            return fields
        }
    }
}
